// ThemeList.tsx
// テーマ一覧表示コンポーネント

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Avatar,
  Chip,
  Checkbox,
  TextField,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import { ExpandMore as ChevronDown, ExpandLess as ChevronUp } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { ThemeType } from '../data/ThemeType';
import { iconMap } from '../../HomePage/Components/ApplicationSelect/iconMap';
import ThemeDialog from './ThemeDialog';
import theme from '../../../style/theme';
import { getThemes, deleteTheme } from '../../../service/ThemeTypeService';

// テーブルのスタイルを設定
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: 'nowrap',
    padding: '6px 20px',
  },
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: 'nowrap',
    padding: '6px 20px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const columnLabels: Record<string, string> = {
  themeName: 'テーマ名',
  themeDescription: '説明',
  preview: 'プレビュー',
};

/**
 * テーマ一覧コンポーネント
 * - テーマを検索・ソート・ページング付きのテーブルで一覧表示する
 * - 行クリックで編集ダイアログを開き、チェックボックスで一括削除できる
 */
export default function ThemeList() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [themes, setThemes] = useState<ThemeType[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ThemeType | null>(null);
  const [loading, setLoading] = useState(true);

  // 検索とフィルタリング
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('searchText_ThemeManagement') || '';
  });
  const [sortConfig, setSortConfig] = useState<{ key: string; asc: boolean }>({
    key: 'themeName',
    asc: true,
  });
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  // 表示設定
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem('visibleColumns_ThemeManagement');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn('visibleColumns の復元に失敗しました:', e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // ページング
  const defaultRowPerPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    return Number(sessionStorage.getItem('showItems_ThemeManagement')) || defaultRowPerPage;
  });
  const rowPerPageList = [50, 100, 1000];
  const [page, setPage] = useState(() => {
    const stored = sessionStorage.getItem('paging_ThemeManagement');
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });

  // 検索設定
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [columnSearchTerms, setColumnSearchTerms] = useState<Record<string, string>>({});

  // 選択と削除
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // 初期データ取得
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const themesData = await getThemes();
      setThemes(themesData);
      setLoading(false);
    };
    fetchData();
  }, []);

  // 検索設定をsessionStorageから復元
  useEffect(() => {
    const savedTerms = sessionStorage.getItem('columnSearchTerms_ThemeManagement');
    if (savedTerms) {
      try {
        const parsedTerms = JSON.parse(savedTerms);
        if (typeof parsedTerms === 'object' && parsedTerms !== null) {
          const sanitizedTerms = Object.fromEntries(
            Object.entries(parsedTerms).filter(
              ([_, v]) => typeof v === 'string' && v !== ''
            )
          ) as Record<string, string>;
          setColumnSearchTerms(sanitizedTerms);
        }
      } catch (e) {
        console.warn('Invalid sessionStorage value for columnSearchTerms');
      }
    }
  }, []);

  // 列の表示切替
  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem('visibleColumns_ThemeManagement', JSON.stringify(Array.from(newSet)));
    setVisibleColumns(newSet);
  };

  // フィルタリングとソート
  const filteredData = useMemo(() => {
    let result = [...themes];

    // 全体検索キーワード
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length > 0) {
      result = result.filter((row) =>
        keywords.every((keyword) =>
          Object.values(row).some((value) => {
            if (typeof value === 'string') {
              return value.toLowerCase().includes(keyword);
            }
            return false;
          })
        )
      );
    }

    // 項目別検索
    const activeColumnSearches = Object.entries(columnSearchTerms).filter(
      ([_, value]) => value.trim() !== ''
    );
    if (activeColumnSearches.length > 0) {
      result = result.filter((row) =>
        activeColumnSearches.every(([key, value]) => {
          if (key === 'preview') {
            return true; // プレビュー列は検索対象外
          }
          const cellValue = row[key as keyof ThemeType];
          return typeof cellValue === 'string' && cellValue.toLowerCase().includes(value.toLowerCase());
        })
      );
    }

    // ソート処理
    const { key, asc } = sortConfig;
    if (key !== 'preview') {
      result.sort((a, b) => {
        const aValue = String(a[key as keyof ThemeType] || '');
        const bValue = String(b[key as keyof ThemeType] || '');
        return asc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      });
    }

    setPage(1);
    return result;
  }, [searchTerm, columnSearchTerms, themes, sortConfig]);

  // ページング
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // 列別検索の変更処理
  const handleColumnSearchChange = (key: string, value: string) => {
    const updated = {
      ...columnSearchTerms,
      [key]: value,
    };
    setColumnSearchTerms(updated);
    const allEmpty = Object.values(updated).every((v) => v === '');
    if (allEmpty) {
      sessionStorage.removeItem('columnSearchTerms_ThemeManagement');
    } else {
      sessionStorage.setItem('columnSearchTerms_ThemeManagement', JSON.stringify(updated));
    }
  };

  // フィルターリセット
  const filterReset = () => {
    sessionStorage.removeItem('searchText_ThemeManagement');
    sessionStorage.removeItem('showItems_ThemeManagement');
    sessionStorage.removeItem('visibleColumns_ThemeManagement');
    sessionStorage.removeItem('columnSearchTerms_ThemeManagement');
    sessionStorage.removeItem('paging_ThemeManagement');

    setSearchTerm('');
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setRowsPerPage(defaultRowPerPage);
    setColumnSearchTerms({});
    setPage(1);
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    setSearchTerm('');
    sessionStorage.removeItem('searchText_ThemeManagement');
  };

  // 編集ボタンクリック
  const handleEdit = (theme: ThemeType) => {
    setSelectedTheme(theme);
    setOpenDialog(true);
  };

  // 削除処理
  const handleDelete = async () => {
    const deletePromises = Array.from(selectedRows).map((id) => deleteTheme(id));
    const results = await Promise.all(deletePromises);

    if (results.every((result) => result)) {
      // 削除成功後、データを再取得
      const themesData = await getThemes();
      setThemes(themesData);
      setSelectedRows(new Set());
      setShowDeleteDialog(false);
    } else {
      alert('削除に失敗しました');
    }
  };

  // 新規追加ボタンクリック
  const handleAdd = () => {
    setSelectedTheme(null);
    setOpenDialog(true);
  };

  // ダイアログ保存処理
  const handleSave = async (updatedTheme: ThemeType) => {
    // この関数は ThemeDialog から呼ばれ、ThemeDialog 内で API 呼び出しが完了している
    // ここでは単にデータを再取得してダイアログを閉じる
    const themesData = await getThemes();
    setThemes(themesData);
    setOpenDialog(false);
  };

  // ダイアログキャンセル処理
  const handleCancel = () => {
    setOpenDialog(false);
    // ダイアログの閉じるアニメーション後にselectedThemeをクリア
  };

  // ローディング中の表示
  if (loading) {
    return (
      <div className="p-4">
        <Typography>読み込み中...</Typography>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Box display="flex" alignItems="center" gap={2}>
        {/* 検索欄 */}
        <div className="mb-2 flex items-center gap-2">
          <TextField
            label="検索"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (value === '') {
                sessionStorage.removeItem('searchText_ThemeManagement');
              } else {
                sessionStorage.setItem('searchText_ThemeManagement', value);
              }
            }}
            className="w-64"
            sx={{ ml: 1, mt: 2, width: 220 }}
            slotProps={{
              input: {
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleFilterTextClear}
                      edge="end"
                      sx={{
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <ClearSharpIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>
        {/* リセットボタン */}
        <Chip
          label="リセット"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            ml: 1,
            mt: 2,
          }}
          onClick={filterReset}
        />
      </Box>

      {/* 表示設定 */}
      <Button
        sx={{ ml: 1, mt: 1, typography: 'buttonMedium' }}
        onClick={() => setShowColumnSettings(!showColumnSettings)}
        startIcon={showColumnSettings ? <ChevronUp /> : <ChevronDown />}
      >
        表示設定
      </Button>

      <Box sx={{ pl: 2, pb: 1 }}>
        {showColumnSettings && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* 表示件数 */}
            <Box sx={{ mb: 0 }}>
              <div style={{ fontWeight: 'bold', color: 'grey', marginBottom: -6 }}>表示件数:</div>
              <RadioGroup
                row
                value={rowsPerPage.toString()}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  sessionStorage.setItem('showItems_ThemeManagement', e.target.value);
                  setPage(1);
                  sessionStorage.removeItem('paging_ThemeManagement');
                }}
              >
                {rowPerPageList.map((num) => (
                  <FormControlLabel
                    key={num}
                    value={num.toString()}
                    control={<Radio sx={{ transform: 'scale(0.6)' }} />}
                    label={`${num}件`}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* 表示項目 */}
            <Box>
              <div className="font-semibold mb-2" style={{ fontWeight: 'bold', color: 'grey', marginBottom: 4 }}>
                表示項目:
              </div>
              <Box className="flex flex-wrap gap-2">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(key)}
                      onChange={() => toggleColumn(key)}
                    />
                    {label}
                  </label>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* 検索設定 */}
      <Button
        sx={{ ml: 1, mt: -1, typography: 'buttonMedium' }}
        onClick={() => setShowSearchSettings(!showSearchSettings)}
        startIcon={showSearchSettings ? <ChevronUp /> : <ChevronDown />}
      >
        検索設定
      </Button>

      <Box sx={{ pl: 2, pb: 2 }}>
        {showSearchSettings && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(columnLabels)
              .filter(([key]) => key !== 'preview') // プレビューは検索対象外
              .map(([key, label]) => (
                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', minWidth: 100 }}>
                  <div style={{ fontSize: '1rem', marginBottom: 4 }}>{label}</div>
                  <TextField
                    size="small"
                    variant="outlined"
                    value={columnSearchTerms[key] || ''}
                    onChange={(e) => handleColumnSearchChange(key, e.target.value)}
                    sx={{
                      width: 140,
                      '& input': {
                        padding: '4px 8px',
                      },
                    }}
                    InputProps={{
                      startAdornment: columnSearchTerms[key] ? undefined : (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              ))}
          </Box>
        )}
      </Box>

      {/* 新規作成ボタンと削除ボタン */}
      <Box sx={{ mt: 0, mb: 0, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          sx={{ ml: 1 }}
          onClick={handleAdd}
        >
          テーマ新規追加
        </Button>
        {selectedRows.size > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteDialog(true)}
          >
            削除
          </Button>
        )}
      </Box>

      {/* ページング */}
      <Box sx={{ ml: 2, my: 1, display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
        <div style={{ marginBottom: 1, color: '#666', fontSize: '1rem' }}>
          全 {filteredData.length} 件中{' '}
          {filteredData.length === 0
            ? '0 件 〜 0 件'
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(page * rowsPerPage, filteredData.length)} 件`}{' '}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              sessionStorage.setItem('paging_ThemeManagement', value.toString());
            }}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: '20px',
                height: '20px',
                padding: '1px',
              },
            }}
          />
        </div>
      </Box>

      {/* テーブル */}
      <TableContainer
        component={Paper}
        sx={{
          ml: isMobile ? 0 : 1,
          mb: 2,
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table sx={{ tableLayout: 'auto', minWidth: 800 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              {/* チェックボックス列 */}
              <StyledTableCell>
                <Tooltip title={selectedRows.size === themes.length ? 'すべて選択解除' : 'すべて選択'}>
                  <Checkbox
                    checked={selectedRows.size === themes.length && themes.length > 0}
                    onChange={() => {
                      if (selectedRows.size === themes.length) {
                        setSelectedRows(new Set());
                      } else {
                        setSelectedRows(new Set(themes.map((row) => row.id)));
                      }
                    }}
                  />
                </Tooltip>
              </StyledTableCell>

              {/* 動的列ヘッダー */}
              {Object.entries(columnLabels).map(([key, label]) => {
                if (!visibleColumns.has(key)) return null;
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;
                const isSortable = key !== 'preview';

                return (
                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      if (!isSortable) return;
                      setPage(1);
                      setSortConfig((prev) =>
                        prev?.key === key ? { key, asc: !prev.asc } : { key, asc: true }
                      );
                    }}
                    onMouseEnter={() => isSortable && setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: isSortable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                  >
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {label}
                      {isSortable && (isActive || hoveredColumn === key) &&
                        (asc && isActive ? (
                          <ChevronUp fontSize="small" className="ml-1 text-gray-500" />
                        ) : !asc && isActive ? (
                          <ChevronDown fontSize="small" className="ml-1 text-gray-500" />
                        ) : (
                          <ChevronDown fontSize="small" className="ml-1 text-gray-300" />
                        ))}
                    </div>
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.map((row) => {
              const IconComponent = iconMap[row.iconName] || iconMap['MoreHorizRoundedIcon'];
              return (
                <StyledTableRow
                  key={row.id}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                    handleEdit(row);
                  }}
                  sx={{
                    height: '48px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: 'inset 0 0 0 1px #90caf9',
                    },
                  }}
                  title="編集"
                >
                  {/* チェックボックスセル */}
                  <StyledTableCell>
                    <Checkbox
                      checked={selectedRows.has(row.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newSet = new Set(selectedRows);
                        if (newSet.has(row.id)) {
                          newSet.delete(row.id);
                        } else {
                          newSet.add(row.id);
                        }
                        setSelectedRows(newSet);
                      }}
                    />
                  </StyledTableCell>

                  {/* テーマ名 */}
                  {visibleColumns.has('themeName') && (
                    <StyledTableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {row.themeName}
                      </Typography>
                    </StyledTableCell>
                  )}

                  {/* 説明 */}
                  {visibleColumns.has('themeDescription') && (
                    <StyledTableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.themeDescription || '-'}
                      </Typography>
                    </StyledTableCell>
                  )}

                  {/* プレビュー */}
                  {visibleColumns.has('preview') && (
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: row.iconBgColor,
                            width: 28,
                            height: 28,
                            border: `2px solid ${row.iconBdColor || row.iconBgColor}`,
                          }}
                        >
                          <IconComponent
                            style={{
                              color: row.iconColor,
                              width: 18,
                              height: 18,
                            }}
                          />
                        </Avatar>
                        <Chip
                          label="ボタン"
                          size="small"
                          sx={{
                            bgcolor: row.buttonColor,
                            color: row.fontColor,
                            border: `1px solid ${row.buttonBdColor || row.buttonColor}`,
                            borderRadius: '6px',
                            height: '28px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            px: 1.5,
                            '& .MuiChip-label': {
                              px: 0.5,
                            },
                          }}
                        />
                      </Box>
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページング（下部） */}
      <Box sx={{ ml: 2, mb: 1, display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
        <div style={{ marginTop: 1, color: '#666', fontSize: '1rem' }}>
          全 {filteredData.length} 件中{' '}
          {filteredData.length === 0
            ? '0 件 〜 0 件'
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(page * rowsPerPage, filteredData.length)} 件`}{' '}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: '20px',
                height: '20px',
                padding: '1px',
              },
            }}
          />
        </div>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            選択された {selectedRows.size} 件のテーマを削除しますか?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* テーマ編集・新規追加ダイアログ */}
      <ThemeDialog
        open={openDialog}
        theme={selectedTheme}
        onSave={handleSave}
        onCancel={handleCancel}
        onExited={() => setSelectedTheme(null)}
      />
    </div>
  );
}
